/**
 * 席次調整的全部算術與文案決策，抽成純函式：沒有 React、沒有 `t()`、沒有 `Date.now()`。
 * 回傳的是 i18n key ＋插值參數，翻譯由呼叫端做，所以同一份邏輯可以同時餵給
 * 「調整席次」預覽行、收據 modal，以及「滿席時邀請自動加開一席」的確認文案。
 *
 * 金額公式逐字對齊後端 `team_billing_controller.updateSeats`：
 *
 *   perSeat  = subscription.perSeatPriceTwd     // 鎖定價；年繳是年價
 *   fraction = remainingFraction(periodStart, periodEnd, now)
 *   charge   = max(1, round(perSeat × added × fraction))
 *
 * 折抵額一律用 `full − charge` 回推，不獨立再算一次：後端只 round 最終金額，
 * 獨立計算的折抵會因為四捨五入讓收據三列加不起來。
 */

import type { SubscriptionCycle } from '@kiao-voice/core'
import { formatTaipeiDate } from '../../lib/taipei-date'

/** 與 `apps/api/app/validators/billing.ts` 的 `seats` 上限同值。 */
export const MAX_TEAM_SEATS = 500

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * 月繳、年繳的本期最長 366 天。超過就是資料異常（例如本機示範資料把期末設在 2099），
 * 這時金額照後端公式算仍然正確，但「本期剩約 26409 天」這種句子不該出現，文案改成
 * 不提天數的版本。
 */
export const MAX_PLAUSIBLE_PERIOD_DAYS = 400

/**
 * 千分位金額（`NT$` 前綴由 i18n 字串提供）。分隔符在中英文都一樣，所以固定用
 * `en-US` 而不是跟著 locale 走——跟著 locale 會讓同一頁的數字格式在兩種語言下漂移。
 */
export function formatTwd(value: number): string {
  return value.toLocaleString('en-US')
}

export type SeatAdjustmentDirection = 'increase' | 'decrease' | 'cancel' | 'noop'

export type { SubscriptionCycle }

export interface SeatAdjustmentInput {
  /** 目前已付費席次（`subscription.seats`）。 */
  seats: number
  /** 已排定的下次續訂席次；`null` ＝ 沒有排程。 */
  pendingSeats: number | null
  /** 使用中＋邀請中的席位數（`seatUsage.used`）——席次的下界。 */
  used: number
  /** 使用者輸入的目標席次。 */
  target: number
  /** 每席每 `cycle` 的鎖定價；`null` ＝ 伺服器算不出來，一律不顯示金額。 */
  perSeatPriceTwd: number | null
  cycle: SubscriptionCycle
  /** ISO 字串，來自 `subscription.currentPeriodStart`。 */
  periodStart: string | null
  /** ISO 字串，來自 `subscription.currentPeriodEnd`。 */
  periodEnd: string | null
  /** 現在時間（epoch ms）——注入以保持純函式。 */
  now: number
  /** 訂閱已排定本期結束後不再續訂（擁有者取消或退款後）。加席仍會扣本期的錢，但席位只到期末。 */
  cancelAtPeriodEnd?: boolean
}

/** 一次加席的完整金額拆解。三個數字永遠滿足 `full − charge === discount`。 */
export interface SeatProration {
  /** 加開的席數。 */
  added: number
  /** 這些席次「整個 cycle」的原價，預覽的錨點數字。 */
  full: number
  /** 本期實際扣款（後端同一條公式）。 */
  charge: number
  /** 本期沒用到的部分，`full − charge` 回推。 */
  discount: number
  /** 本期總天數（四捨五入到整天，只給文案用）。 */
  daysTotal: number
  /** 本期剩餘天數（無條件進位到整天，只給文案用）。 */
  daysLeft: number
  /** 本期已過天數，`daysTotal − daysLeft`。 */
  daysUsed: number
  /** 天數是否合理可展示（1～`MAX_PLAUSIBLE_PERIOD_DAYS` 天）；false 時文案不提天數。 */
  daysKnown: boolean
}

export interface SeatCopy {
  key: string
  params: Record<string, string | number>
}

export interface SeatAdjustment {
  direction: SeatAdjustmentDirection
  /** clamp 後的目標席次。 */
  target: number
  /** 送出按鈕的 i18n key。 */
  ctaKey: string
  /** 預覽行的 i18n key ＋參數；`noop` 時為 `null`。 */
  preview: SeatCopy | null
  /**
   * 加席會讓後端把已排定的減席一併清掉（`finalizeSeatAddon` 與
   * `team_billing_controller.updateSeats` 都把 `pendingSeats` 設回 `null`），所以
   * `increase` 且 `pendingSeats !== null` 時必須在預覽行與收據的「下次續訂起」列
   * 明說那筆排程沒了——否則排定 14 席的擁有者加 1 席會變成續訂 17 席而沒被告知。
   * 其他方向一律 `null`。
   */
  pendingCancelNotice: SeatCopy | null
  /**
   * 訂閱不會再續訂時（`cancelAtPeriodEnd`）加席的提醒：收據不能承諾「下次續訂起每月 NT$X」，
   * 剛買的席位只到本期結束。`increase` 且 `cancelAtPeriodEnd` 才有值。
   */
  noRenewalNotice: SeatCopy | null
  /**
   * 加席金額。只有 `direction === 'increase'` 且伺服器同時給了鎖定價與本期起訖
   * 才會有值；任一缺少就是 `null`，UI 必須完全不顯示金額（不要退回方案定價，
   * 那個值對年繳與鎖舊價的客戶是錯的）。
   */
  amount: SeatProration | null
  /** 目標值已經壓在下界（`used`）——UI 據此顯示「不能再低」的赭石提醒。 */
  atFloor: boolean
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/**
 * 續訂日的文案用字串：台灣日曆日 `YYYY/MM/DD`（與 /app/account 同一支 formatter），
 * 缺值或解析失敗回 `null` 讓呼叫端改用不帶日期的句子。
 */
export function toRenewalDate(iso: string | null): string | null {
  if (!iso) return null
  if (!Number.isFinite(Date.parse(iso))) return null
  return formatTaipeiDate(iso)
}

/**
 * 加席的按比例金額。缺鎖定價或缺本期起訖時回 `null`（呼叫端不顯示任何金額）。
 */
export function computeSeatProration(
  added: number,
  input: Pick<SeatAdjustmentInput, 'perSeatPriceTwd' | 'periodStart' | 'periodEnd' | 'now'>
): SeatProration | null {
  const { perSeatPriceTwd, periodStart, periodEnd, now } = input
  if (added <= 0) return null
  if (perSeatPriceTwd === null || !Number.isFinite(perSeatPriceTwd)) return null
  if (!periodStart || !periodEnd) return null
  const start = Date.parse(periodStart)
  const end = Date.parse(periodEnd)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null

  const full = perSeatPriceTwd * added
  const span = end - start
  const fraction = span <= 0 ? 1 : clamp01((end - now) / span)
  const charge = Math.max(1, Math.round(full * fraction))
  const daysTotal = span <= 0 ? 0 : Math.round(span / MS_PER_DAY)
  const daysLeft = Math.min(daysTotal, Math.max(0, Math.ceil((end - now) / MS_PER_DAY)))

  return {
    added,
    full,
    charge,
    discount: full - charge,
    daysTotal,
    daysLeft,
    daysUsed: daysTotal - daysLeft,
    daysKnown: daysTotal > 0 && daysTotal <= MAX_PLAUSIBLE_PERIOD_DAYS,
  }
}

/**
 * 判定方向並產出該方向需要的數字與文案 key。
 *
 * 方向以「目前席次」與「已排定席次」兩個基準一起看：
 * - `target > seats` → `increase`（立即扣款）
 * - `target < seats` → `decrease`（排定下次續訂）
 * - `target === seats` 且有排程 → `cancel`（取消減席排程）
 * - 其餘（含目標值就是已排定值、非整數、超出 `[used, MAX_TEAM_SEATS]`）→ `noop`
 */
export function resolveSeatAdjustment(input: SeatAdjustmentInput): SeatAdjustment {
  const { seats, pendingSeats, used, target, cycle } = input
  const scheduled = pendingSeats ?? seats
  const atFloor = target <= used
  const valid =
    Number.isInteger(target) && target >= used && target <= MAX_TEAM_SEATS && target >= 1

  const noop: SeatAdjustment = {
    direction: 'noop',
    target,
    ctaKey: 'team.seatAdjustCta',
    preview: null,
    pendingCancelNotice: null,
    noRenewalNotice: null,
    amount: null,
    atFloor,
  }
  if (!valid || target === scheduled) return noop

  if (target > seats) {
    const added = target - seats
    const amount = computeSeatProration(added, input)
    const yearly = cycle === 'yearly'
    const preview: SeatCopy = amount
      ? {
          key: amount.daysKnown
            ? yearly
              ? 'team.seatPreviewIncreaseYearly'
              : 'team.seatPreviewIncreaseMonthly'
            : yearly
              ? 'team.seatPreviewIncreaseNoDaysYearly'
              : 'team.seatPreviewIncreaseNoDaysMonthly',
          params: {
            added,
            full: amount.full,
            charge: amount.charge,
            daysLeft: amount.daysLeft,
            daysTotal: amount.daysTotal,
          },
        }
      : { key: 'team.seatPreviewIncreaseNoAmount', params: { added } }
    return {
      direction: 'increase',
      target,
      ctaKey: 'team.seatAdjustIncreaseCta',
      preview,
      pendingCancelNotice:
        pendingSeats === null
          ? null
          : {
              key: 'team.seatPreviewIncreaseCancelsPending',
              params: { pending: pendingSeats, target },
            },
      noRenewalNotice: input.cancelAtPeriodEnd
        ? {
            key: 'team.seatPreviewIncreaseNoRenewal',
            params: { target, date: toRenewalDate(input.periodEnd) ?? '' },
          }
        : null,
      amount,
      atFloor,
    }
  }

  if (target < seats) {
    const renewalDate = toRenewalDate(input.periodEnd)
    return {
      direction: 'decrease',
      target,
      ctaKey: 'team.seatAdjustDecreaseCta',
      preview: {
        key: renewalDate ? 'team.seatPreviewDecrease' : 'team.seatPreviewDecreaseNoDate',
        params: { removed: seats - target, target, seats, date: renewalDate ?? '' },
      },
      pendingCancelNotice: null,
      noRenewalNotice: null,
      amount: null,
      atFloor,
    }
  }

  // target === seats with a schedule in place → cancel it.
  return {
    direction: 'cancel',
    target,
    ctaKey: 'team.seatAdjustCancelCta',
    preview: { key: 'team.seatPreviewCancel', params: { seats } },
    pendingCancelNotice: null,
    noRenewalNotice: null,
    amount: null,
    atFloor,
  }
}
